import React from 'react';
import { cn } from '@/lib/utils';

export type StatCardStatus = 'ok' | 'warning' | 'critical' | 'neutral';

export interface StatCardProps {
  label: string;
  value: string | number;
  /** Optional delta or secondary line (e.g. "+2d", "vs baseline") */
  delta?: string | null;
  status?: StatCardStatus;
  /** Optional drill-through (e.g. Link or button) */
  action?: React.ReactNode;
  className?: string;
}

const statusStyles: Record<StatCardStatus, string> = {
  ok: 'border-l-primary/60',
  warning: 'border-l-amber-500/70',
  critical: 'border-l-destructive/70',
  neutral: 'border-l-border',
};

/**
 * Compact metric tile. Console density: tight padding, tabular-nums, no shadow.
 */
export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  delta,
  status = 'neutral',
  action,
  className,
}) => {
  return (
    <div
      className={cn(
        'rounded-md border border-border bg-card px-3 py-2 border-l-[3px] min-w-0',
        statusStyles[status],
        className
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-medium text-muted-foreground truncate leading-tight">
            {label}
          </p>
          <p className="mt-0.5 text-sm font-semibold tabular-nums text-foreground leading-tight">
            {value}
          </p>
          {delta != null && delta !== '' && (
            <p className="mt-0.5 text-[11px] text-muted-foreground tabular-nums">
              {delta}
            </p>
          )}
        </div>
        {action != null && <div className="shrink-0 mt-0.5">{action}</div>}
      </div>
    </div>
  );
};

export default StatCard;
